import { useEffect, useState, useCallback, RefObject } from 'react';

export const usePictureInPicture = (
  videoRef: RefObject<HTMLVideoElement>,
  isVideo: boolean,
  mediaUrl: string | null
) => {
  const [isPiPActive, setIsPiPActive] = useState(false);

  // Check if PiP is supported - just check if it's a video and API exists
  const isPiPSupported = isVideo && 'pictureInPictureEnabled' in document;

  // Track PiP state changes
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;

    const videoElement = videoRef.current;

    const handleEnterPiP = () => setIsPiPActive(true);
    const handleLeavePiP = () => setIsPiPActive(false);

    videoElement.addEventListener('enterpictureinpicture', handleEnterPiP);
    videoElement.addEventListener('leavepictureinpicture', handleLeavePiP);

    // Also poll to catch cases where events don't fire (e.g., browser's "Back to tab" button)
    const pollInterval = setInterval(() => {
      const isInPiP = document.pictureInPictureElement === videoElement;
      setIsPiPActive(isInPiP);
    }, 200);

    return () => {
      videoElement.removeEventListener('enterpictureinpicture', handleEnterPiP);
      videoElement.removeEventListener('leavepictureinpicture', handleLeavePiP);
      clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideo, mediaUrl]);

  const togglePiP = useCallback(async () => {
    if (!isVideo || !videoRef.current) return;

    const videoElement = videoRef.current;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiPActive(false);
      } else {
        await videoElement.requestPictureInPicture();
        setIsPiPActive(true);
      }
    } catch (err) {
      console.error('Error toggling Picture-in-Picture:', err);
    }
  }, [isVideo, videoRef]);

  return {
    isPiPActive,
    isPiPSupported,
    togglePiP,
  };
};
